import React from "react";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const airportRoutes = {
  gatwick: { airport: "Gatwick", terminals: "North and South terminals", corridor: "M25 and M23", distance: "approximately 40–50 miles", duration: "usually 55–90 minutes, depending on M25 and M23 traffic", liveStripVariant: "gatwick" },
  luton: { airport: "Luton", terminals: "the main terminal", corridor: "M25 and M1", distance: "approximately 35–45 miles", duration: "usually 45–75 minutes, depending on M25 and M1 traffic", liveStripVariant: "luton" },
  stansted: { airport: "Stansted", terminals: "the main terminal", corridor: "M25 and M11", distance: "approximately 60–70 miles", duration: "usually 75–110 minutes, depending on M25 and M11 traffic", liveStripVariant: "stansted" },
};

const buildRoute = (key) => {
  const data = airportRoutes[key];
  const airport = data.airport;
  const path = `/slough-to-${key}`;
  const routeName = `Slough to ${airport}`;
  const faqItems = [
    { question: `How do I get an accurate Slough to ${airport} taxi quote?`, answer: "Enter the exact pickup, journey time, passengers and luggage. We will check the route and driver availability before confirming our best available fixed rate." },
    { question: `How long does a taxi from Slough to ${airport} take?`, answer: `The journey is ${data.duration}. Leave additional time for peak traffic and airport check-in.` },
    { question: "Can I request an MPV or minibus for luggage?", answer: "Yes. Include the exact passenger and bag count so we can check the right saloon, MPV or group vehicle rather than confirming a vehicle that is too small." },
    { question: "Is payment required when I send the enquiry?", answer: "No. Sending the journey details is an enquiry only. We confirm availability and the final rate before anything is booked or paid." },
  ];

  return {
    id: `slough-to-${key}`,
    path,
    liveStripVariant: data.liveStripVariant,
    hideLiveStatus: true,
    seo: {
      title: `${routeName} Taxi – Airport Transfer Quotes | CheapCabGrab`,
      description: `Request a Slough to ${airport} taxi quote for saloon, MPV and group vehicles. Licensed drivers, luggage checks and no payment when you enquire.`,
      keywords: `${routeName} taxi, taxi from Slough to ${airport} Airport, Slough airport transfer, SL taxi to ${airport}, CheapCabGrab`,
      canonical: `https://www.cheapcabgrab.com${path}`,
      ogTitle: `${routeName} Taxi – Airport Transfer Quotes`,
      ogDescription: `Request an accurate ${routeName} airport transfer quote with passenger, luggage and driver availability checked.`,
      serviceName: `CheapCabGrab – ${routeName} Taxi`,
      routeName: `${routeName} Taxi`,
      areaServed: [`${airport} Airport`, "Slough SL1", "Slough SL2", "Slough SL3", "Slough SL4", "Slough SL5", "Slough SL6"],
      offerCatalogName: `${routeName} Taxi Routes`,
      offerName: `${routeName} airport taxi quote`,
      offerDescription: `Taxi and private-hire enquiries from Slough postcodes to ${airport} Airport with passenger and luggage requirements checked.`,
      reservationName: `Taxi enquiry from Slough to ${airport}`,
    },
    hero: {
      eyebrow: `${routeName} airport taxi`,
      h1: `${routeName} Taxi – Accurate Airport Transfer Quotes`,
      subtitle: `${data.corridor} route planning for ${data.terminals}.`,
      body: "Tell us your exact passengers and luggage. We’ll check a suitable licensed driver and our best available fixed rate today—no payment or obligation when you enquire.",
    },
    quote: { label: "Get an accurate journey quote", note: "Free • Fast • No obligation", presetPickup: "Slough, SL1", presetDropoff: `${airport} Airport` },
    quotePromises: ["Accurate fixed-rate check", "Passenger and luggage matched", "Licensed driver availability", "No payment when enquiring"],
    flags: ["✓ Best available rate checked for your journey", `✓ ${airport} airport transfer experience`, "✓ Saloon, MPV and group vehicle options", "✓ No payment or booking commitment today"],
    reviewPageKey: path,
    reviewAriaLabel: `${routeName} customer reviews`,
    floatingCta: "Get my accurate quote",
    mobileCta: "Get my accurate quote",
    supportNudge: `Need help with ${routeName}?`,
    faqItems,
    generatedContent: {
      overviewTitle: `${routeName} at a glance`,
      overviewIntro: `A pre-booked airport transfer from Slough to ${airport}, with the vehicle and final rate checked against your actual journey details.`,
      overviewCards: [
        { title: "Journey planning", items: [`Distance: ${data.distance}`, `Typical drive: ${data.duration}`, `Main corridor: ${data.corridor}`, `Destination: ${airport} Airport, ${data.terminals}`] },
        { title: "What we confirm", items: ["Exact passenger and luggage capacity", "Pickup time and route practicality", "Suitable licensed driver availability", "Best available fixed rate before booking"] },
        { title: "Useful for", items: ["Homes and hotels across SL postcodes", "Families travelling with luggage", "Early departures and late arrivals", "Business and group airport journeys"] },
      ],
      planningTitle: `Plan your Slough to ${airport} pickup`,
      planningIntro: "Accurate details help us match the right vehicle and avoid last-minute luggage or capacity problems.",
      planningCards: [
        { title: "Before requesting the quote", body: "Have the complete pickup postcode, flight time and preferred collection time ready.", items: ["Count checked and cabin bags", "Include children in passenger numbers", "Mention child seats, wheelchairs or extra stops"] },
        { title: "Allow enough travel time", body: `Traffic on the ${data.corridor} can vary considerably. Build in airport check-in time and a sensible road buffer.`, items: ["Tell us the flight departure time", "Avoid relying on the shortest map estimate", "Confirm the terminal before travel"] },
      ],
      links: [
        { to: "/terminalFinder", title: "Check your airport terminal", body: "Confirm the airline terminal before finalising the pickup time.", label: "Open terminal finder" },
        { to: "/minibus", title: "Travelling as a group?", body: "See minibus options when passenger and luggage needs exceed a standard taxi.", label: "View minibus options" },
        { to: "/contact", title: "Complex journey?", body: "Ask about extra stops, accessibility requirements or unusual luggage.", label: "Contact the team" },
      ],
      faqTitle: `${routeName} taxi FAQs`,
    },
  };
};

const SloughAirportPremiumRoute = ({ airport }) => (
  <CcgRoutePremiumPage route={buildRoute(airport)} QuoteComponent={PostcodeQuotenew} />
);

export default SloughAirportPremiumRoute;
